import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import SimilarIcons from '../components/SimilarIcons'; 
import CopyFreePNG from '../components/CopyFreePNG'; 

// Utility function to check if code is running in a browser environment
const isBrowser = () => typeof window !== 'undefined';

/**
 * Helper function to format keywords
 */
const formatKeywords = (keywordsInput, count = 3) => {
  if (!keywordsInput) return '';

  let keywordsArray = [];
  if (Array.isArray(keywordsInput)) {
    keywordsArray = keywordsInput;
  } else if (typeof keywordsInput === 'string') {
    keywordsArray = keywordsInput.split(',').map((k) => k.trim());
  }

  return keywordsArray.filter((k) => k).slice(0, count).join(', ');
};

const IconDetail = ({
  slug,
  BACKEND_URL,
  collection,
  initialData,
  imageUrl: propImageUrl,
  accessStatus: initialAccessStatus,
  similarIconsData,
  similarCollections,
  authError = false,
}) => {
  // Client-side only state
  const [clientSideInitialized, setClientSideInitialized] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [DownloadPNGButton, setDownloadPNGButton] = useState(null);
  const [DownloadSVGButton, setDownloadSVGButton] = useState(null);
  const [CopyPNGButton, setCopyPNGButton] = useState(null);
  const [CopySVGButton, setCopySVGButton] = useState(null);
  const [AddToCartButton, setAddToCartButton] = useState(null);
  const [DownloadFreePNG, setDownloadFreePNG] = useState(null);

  // Dynamically import client-side components
  useEffect(() => {
    const loadClientComponents = async () => {
      try {
        const [
          downloadPNGModule,
          downloadSVGModule,
          copyPNGModule,
          copySVGModule,
          addToCartModule,
          downloadFreePNGModule
        ] = await Promise.all([
          import('../components/DownloadPNGButton'),
          import('../components/DownloadSVGButton'),
          import('../components/CopyPNGButton'),
          import('../components/CopySVGButton'),
          import('../components/AddToCartButton'),
          import('../components/DownloadFreePNG')
        ]);

        setDownloadPNGButton(() => downloadPNGModule.default);
        setDownloadSVGButton(() => downloadSVGModule.default);
        setCopyPNGButton(() => copyPNGModule.default);
        setCopySVGButton(() => copySVGModule.default);
        setAddToCartButton(() => addToCartModule.default);
        setDownloadFreePNG(() => downloadFreePNGModule.default);
      } catch (error) {
        console.error("Error loading client components:", error);
      }
    };

    if (isBrowser()) {
      loadClientComponents();
    }
  }, []);

  // Initialize on client-side only with longer delay to prevent visual jumps
  useEffect(() => {
    if (isBrowser()) {
      try {
        const timer = setTimeout(() => {
          setLoadingTimeout(true);
        }, 5000);

        const initTimer = setTimeout(() => {
          setClientSideInitialized(true);
        }, 500);

        return () => {
          clearTimeout(timer);
          clearTimeout(initTimer);
        };
      } catch (error) {
        console.error("Error in initialization useEffect:", error);
        setClientSideInitialized(true);
        setLoadingTimeout(true);
      }
    }
  }, []);

  // Check if the user has the icon in their cart (client-side only)
  useEffect(() => {
    const checkCartStatus = async () => {
      if (isBrowser() && clientSideInitialized && initialData?.id) {
        try {
          const cartStoreModule = await import('@/store/cartStore');
          const cartStore = cartStoreModule.default;

          const cartItems = cartStore.getState().cartItems || [];
          const isInCartAlready = cartItems.some(item =>
            item.assetId === initialData.id || item.id === initialData.id
          );

          setIsInCart(isInCartAlready);
        } catch (error) {
          console.error("Error checking cart status:", error);
        }
      }
    };

    checkCartStatus();
  }, [clientSideInitialized, initialData?.id]);

  // Fallback data for missing initialData
  const defaultData = {
    keywords: slug ? [slug] : ['icon'],
    collection: collection || 'basic',
    id: null,
    media_type: collection === 'basic3d' ? 'rastericon' : 'icon',
    error_message: '',
    is_purchased: false,
    is_subscription_active: false,
    is_free: collection === 'basic' || collection === 'basic3d',
    image_url: propImageUrl || '',
  };

  if (!initialData) {
    if (isBrowser() && loadingTimeout) {
      return (
        <div className="p-8 text-center">
          <p className="text-xl">Не удалось загрузить данные иконки.</p>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => {
              if (isBrowser()) {
                window.location.reload();
              }
            }}
          >
            Попробовать снова
          </button>
        </div>
      );
    }

    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
        <p className="text-xl">Loading icon details...</p>
      </div>
    );
  }

  const iconData = initialData
    ? {
        ...defaultData,
        ...initialData,
      }
    : defaultData;

  const {
    keywords: assetKeywords = '',
    collection: iconCollection = collection,
    id: assetId = null,
    media_type: mediaType = iconCollection === 'basic3d' ? 'rastericon' : 'icon',
    error_message: serverErrorMessage = '',
    is_purchased = false,
    is_subscription_active = false,
    is_free = false,
  } = iconData;

  const hasAccess = is_free || is_purchased || is_subscription_active;
  // Приоритизируем url_large для basic3d
  const imageUrl =
    propImageUrl || 
    (iconCollection === 'basic3d' && iconData.url_large ? iconData.url_large : iconData.processedImageUrl || iconData.image_url || '');

  // Calculate price based on collection
  const price =
    iconCollection === 'basic' || iconCollection === 'basic3d'
      ? 0
      : iconCollection === 'outline' || iconCollection === 'polish-blue'
      ? 2
      : 1;

  const handleAddToCart = async () => {
    if (!isBrowser() || !assetId) {
      return;
    }

    try {
      const cartStoreModule = await import('@/store/cartStore');
      const cartStore = cartStoreModule.default;
      const addToCart = cartStore.getState().addToCart;

      if (typeof addToCart !== 'function') {
        alert('Error adding to cart. Please try again.');
        return;
      }

      await addToCart({
        id: assetId,
        assetId,
        product: { price },
        mediaType,
        slug,
        collection: iconCollection,
      });

      setIsInCart(true);
    } catch (error) {
      console.error("Error adding to cart:", error);
      if (isBrowser()) {
        alert('Error adding to cart. Please refresh and try again.');
      }
    }
  };

  // Handler for proceeding to checkout
  const handleCheckout = () => {
    if (isBrowser()) {
      window.location.href = '/checkout';
    }
  };

  // SEO and licensing helpers
  const derivedTitle = formatKeywords(assetKeywords, 3) || slug || 'Icon';
  const pageTitle = `${derivedTitle} ${iconCollection === 'basic3d' ? '3D' : 'vector'} icon`;
  const description = `Beautiful handcrafted ${derivedTitle} ${iconCollection === 'basic3d' ? '3D' : ''} icon`;

  const getLicenseUrl = () => {
    return iconCollection === 'basic' || iconCollection === 'basic3d'
      ? 'https://creativecommons.org/licenses/by/4.0/'
      : 'https://davooda.com/terms';
  };

  const getContentUrl = () => {
    switch (iconCollection) {
      case 'basic':
        return `https://icons-pbl.davooda.com/basic-icons/${slug}.svg`;
      case 'outline':
        return `https://icons-pbl.davooda.com/outline/png-360px/${slug}-360x360.png`;
      case 'polish-blue':
        return `https://icons-pbl.davooda.com/polish-blue/png-360px/${slug}-360x360.png`;
      case 'basic3d':
        return iconData.url_large || `https://icons-pbl.davooda.com/basic3d-icons/png-512px/${slug}.png`;
      default:
        return imageUrl;
    }
  };

  const getCanonicalUrl = () => {
    switch (iconCollection) {
      case 'basic':
        return `https://davooda.com/basic-icons/${slug}`;
      case 'outline':
        return `https://davooda.com/outline/${slug}`;
      case 'polish-blue':
        return `https://davooda.com/polish-blue-icons/${slug}`;
      case 'basic3d':
        return `https://davooda.com/basic3d/${slug}`;
      default:
        return `https://davooda.com/${slug}`;
    }
  };

  const getImageObjectJSON = () => {
    try {
      const firstFour = formatKeywords(assetKeywords, 4);
      const licenseUrl = getLicenseUrl();
      const creditTextPrefix = iconCollection === 'basic' || iconCollection === 'basic3d' ? 'Free' : '';
      const separator = creditTextPrefix ? ' ' : '';
      const iconType = iconCollection === 'basic3d' ? '3D' : 'vector';

      return {
        '@context': 'https://schema.org/',
        '@type': 'ImageObject',
        contentUrl: getContentUrl(),
        license: licenseUrl,
        acquireLicensePage: licenseUrl,
        creditText: `${creditTextPrefix}${separator}${firstFour} ${iconType} icon`.trim(),
        creator: {
          '@type': 'Person',
          name: 'Davooda',
        },
        copyrightNotice: 'Davooda',
      };
    } catch (error) {
      console.error("Error generating ImageObject JSON-LD:", error);
      return {};
    }
  };

  const getBreadcrumbsJSON = () => {
    try {
      let secondItemName = '';
      let secondItemLink = '';

      switch (iconCollection) {
        case 'basic':
          secondItemName = 'Basic Icons';
          secondItemLink = 'https://davooda.com/basic-icons/';
          break;
        case 'outline':
          secondItemName = 'Outline Icons';
          secondItemLink = 'https://davooda.com/outline-icons/';
          break;
        case 'polish-blue':
          secondItemName = 'Polish Blue Icons';
          secondItemLink = 'https://davooda.com/polish-blue-icons/';
          break;
        case 'basic3d':
          secondItemName = 'Basic 3D Icons';
          secondItemLink = 'https://davooda.com/basic3d/';
          break;
        default:
          secondItemName = 'All Icons';
          secondItemLink = 'https://davooda.com/';
      }

      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Icons',
            item: 'https://davooda.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: secondItemName,
            item: secondItemLink,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: slug || 'icon',
            item: `${secondItemLink}${slug || ''}`,
          },
        ],
      };
    } catch (error) {
      console.error("Error generating Breadcrumbs JSON-LD:", error);
      return {};
    }
  };

  // Custom callbacks for download buttons
  const onBeforeAction = () => {
    try {
      return true;
    } catch (error) {
      console.error("Error in onBeforeAction:", error);
      return false;
    }
  };

  const onActionComplete = () => {
    try {
      // Additional actions after successful download
    } catch (error) {
      console.error("Error in onActionComplete:", error);
    }
  };

  // Safely check if user is logged in - moved to client-side only
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (isBrowser() && clientSideInitialized) {
      try {
        setIsLoggedIn(!!window.__NEXT_DATA__?.props?.pageProps?.session?.user);
      } catch (error) {
        console.error("Error checking login status:", error);
        setIsLoggedIn(false);
      }
    }
  }, [clientSideInitialized]);

  // Don't render buttons during SSR or before client initialization
  const shouldRenderButtons =
    isBrowser() &&
    clientSideInitialized &&
    DownloadPNGButton &&
    DownloadSVGButton &&
    CopyPNGButton &&
    CopySVGButton &&
    AddToCartButton &&
    DownloadFreePNG;
    
  // Предварительная загрузка копирования для предотвращения исчезновения кнопки
  const [copyPngInitialized, setCopyPngInitialized] = useState(false);
  
  useEffect(() => {
    if (isBrowser() && clientSideInitialized && CopyPNGButton) {
      setCopyPngInitialized(true);
    }
  }, [clientSideInitialized, CopyPNGButton]);

  // Determine default PNG size based on collection for placeholder text
  const getDefaultPngSize = () => {
    switch (iconCollection) {
      case 'basic':
        return '24x24';
      case 'outline':
        return '48x48';
      case 'polish-blue':
        return '64x64';
      case 'basic3d':
        return '512x512';
      default:
        return '48x48';
    }
  };
  const defaultPngSize = getDefaultPngSize();

  // Проверка, является ли иконка типа "rastericon"
  const isRasterIcon = mediaType === 'rastericon';
  
  // Define the image size based on collection - 20% larger for basic3d
  const isBasic3D = iconCollection === 'basic3d';
  const imageSize = isBasic3D ? 307 : 256; // 256 * 1.2 ≈ 307

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content={
            Array.isArray(assetKeywords)
              ? assetKeywords.join(', ')
              : assetKeywords
          }
        />
        <meta property="og:title" content={derivedTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={derivedTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="creator" content="Davooda" />
        <meta name="copyrightnotice" content="Davooda" />
        <link rel="canonical" href={getCanonicalUrl()} />
        {isBrowser() && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(getImageObjectJSON()),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(getBreadcrumbsJSON()),
              }}
            />
          </>
        )}
      </Head>

      <div className="flex flex-wrap pt-8 gap-16 w-full max-w-7xl">
        {/* Icon Preview - Updated to handle larger size for basic3d */}
        <div className="w-full md:w-1/3 flex justify-center items-center">
          <div className={`w-[${imageSize}px] h-[${imageSize}px] flex items-center justify-center`}>
            {imageUrl && imageUrl.includes('.svg') ? (
              <img
                src={imageUrl}
                alt={derivedTitle}
                className="max-w-full max-h-full object-contain"
                style={{ width: isBasic3D ? '120%' : '100%' }}
              />
            ) : (
              <Image
                src={imageUrl || '/placeholder-icon.png'}
                alt={derivedTitle}
                width={imageSize}
                height={imageSize}
                priority
                className="object-contain"
                unoptimized={imageUrl && imageUrl.includes('.svg')}
              />
            )}
          </div>
        </div>

        {/* Icon Details */}
        <div className="flex-1 min-w-[250px] flex flex-col">
          {/* Breadcrumbs */}
          <nav className="mb-1">
            <ul className="list-none p-0 m-0 flex items-center text-xs">
              <li className="flex items-center">
                <Link href="/" className="text-black underline">
                  Icons
                </Link>
                <span className="mx-2">&#62;</span>
              </li>
              <li>
                <Link
                  href={
                    iconCollection === 'basic'
                      ? '/basic-icons/'
                      : iconCollection === 'outline'
                      ? '/outline-icons/'
                      : iconCollection === 'polish-blue'
                      ? '/polish-blue-icons/'
                      : iconCollection === 'basic3d'
                      ? '/basic3d/'
                      : '/'
                  }
                  className="text-black underline"
                >
                  {iconCollection === 'basic3d' ? 'Basic 3D' : iconCollection} Collection
                </Link>
              </li>
            </ul>
          </nav>

          {/* Title and License */}
          <div className="flex flex-col gap-1">
            <h1 className="text-4xl font-bold m-0 text-[#191919] -tracking-[1px] leading-tight">
              {derivedTitle}
            </h1>
            <p className="text-sm leading-7">
              <b>License:</b>{' '}
              {iconCollection === 'basic' || iconCollection === 'basic3d' ? (
                <a
                  href="https://creativecommons.org/licenses/by/4.0/"
                  className="underline"
                >
                  Creative Commons Attribution 4.0
                </a>
              ) : (
                'Free PNG with attribution, Premium SVG.'
              )}
              <br />
              {!hasAccess && (
                <>
                  <b>Attribution:</b> Required{' '}
                  <Link href="/how-to-attribute" className="underline">
                    How to attribute?
                  </Link>
                </>
              )}
            </p>
          </div>

          {/* Auth error notice */}
          {authError && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
              <p className="font-medium text-yellow-800">
                {serverErrorMessage ||
                  'Server response: Authentication error. Please log in again to access premium icons.'}
              </p>
            </div>
          )}

          {/* Button section */}
          <div className="flex flex-row flex-wrap w-full mb-5 mt-4">
            {isRasterIcon ? (
              /* Упрощенный интерфейс для rastericon */
              <div className="w-full max-w-[300px]">
                {/* Download Free PNG Button */}
                <div className="mb-4">
                  {shouldRenderButtons ? (
                    <DownloadFreePNG
                      slug={slug}
                      collection={iconCollection}
                      url={iconData.url_large}
                      onBeforeAction={onBeforeAction}
                      onActionComplete={onActionComplete}
                    />
                  ) : (
                    <button
                      className="block w-full text-center whitespace-nowrap px-3 py-3 bg-black text-white font-bold shadow-md rounded-md hover:bg-gray-800 transition-colors min-h-[60px]"
                      disabled={true}
                    >
                      Download PNG for free
                    </button>
                  )}
                </div>

                {/* Copy PNG Button */}
                <div className="text-left mt-2">
                  <CopyFreePNG
                    slug={slug}
                    collection={iconCollection}
                    url={iconData.url_large}
                    onBeforeAction={onBeforeAction}
                  />
                </div>
              </div>
            ) : (
              /* Стандартный интерфейс для других типов */
              <>
                <div className="flex-1 md:flex-none md:w-[300px] mr-[5px] md:mr-[10px]">
                  {/* PNG Download Button */}
                  <div className="mb-1">
                    {shouldRenderButtons ? (
                      <DownloadPNGButton
                        assets={[
                          {
                            slug,
                            collection: iconCollection,
                            media_type: mediaType,
                            is_free: iconCollection === 'basic' || iconCollection === 'basic3d',
                            format: ['png'],
                          },
                        ]}
                        archiveName={`${slug}.zip`}
                        is_purchased={is_purchased}
                        isSubscriptionActive={is_subscription_active}
                        onBeforeAction={onBeforeAction}
                        onActionComplete={onActionComplete}
                      />
                    ) : (
                      <div className="inline-block relative w-full">
                        <button
                          className="block w-full text-center whitespace-nowrap px-3 py-3 bg-transparent border border-black shadow-md rounded-md disabled:opacity-50 disabled:cursor-not-allowed min-h-[60px]"
                          disabled={false}
                        >
                          {`Download ${defaultPngSize} PNG`}
                          <span className="ml-2 text-[0.8em] cursor-pointer inline-block rounded-full w-[30px] h-[30px] leading-[33px] text-center select-none transition-colors duration-200 bg-transparent">
                            ▼
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Copy PNG */}
                  <div className="text-left mt-3">
                    {shouldRenderButtons ? (
                      <CopyPNGButton
                        assets={[
                          {
                            slug,
                            collection: iconCollection,
                            media_type: mediaType,
                            is_free: iconCollection === 'basic' || iconCollection === 'basic3d',
                            format: ['png'],
                          },
                        ]}
                        is_purchased={is_purchased}
                        isSubscriptionActive={is_subscription_active}
                        onBeforeAction={onBeforeAction}
                      />
                    ) : (
                      <button className="inline-flex items-center text-black hover:underline cursor-pointer">
                        <span>Copy PNG</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 md:flex-none md:w-[300px]">
                  {/* SVG Button */}
                  <div className="mb-1">
                    <div className="relative min-h-[60px] w-full">
                      <div className="absolute w-full h-full z-10">
                        <button
                          className="absolute w-full h-full border-none rounded-md bg-black text-white font-bold pl-5 text-base"
                          style={{ transform: 'none' }}
                        >
                          {(iconCollection === 'basic' || is_purchased || is_subscription_active)
                            ? 'Download SVG'
                            : `Add SVG to Cart $${price.toFixed(2)}`}
                        </button>
                      </div>

                      {shouldRenderButtons && (
                        <div className="absolute w-full h-full z-20">
                          {(iconCollection === 'basic' || is_purchased || is_subscription_active) ? (
                            <DownloadSVGButton
                              assets={[
                                {
                                  slug,
                                  collection: iconCollection,
                                  media_type: mediaType,
                                  is_free: iconCollection === 'basic',
                                  format: ['svg'],
                                },
                              ]}
                              archiveName={`${slug}.svg`}
                              is_purchased={is_purchased}
                              isSubscriptionActive={is_subscription_active}
                              onBeforeAction={onBeforeAction}
                              onActionComplete={onActionComplete}
                            />
                          ) : (
                            <AddToCartButton
                              isInCart={isInCart}
                              slug={slug}
                              price={price}
                              collection={iconCollection}
                              media_type={mediaType}
                              onAddToCart={handleAddToCart}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Copy SVG Button */}
                  <div className="text-left mt-3">
                    {(iconCollection === 'basic' || is_purchased || is_subscription_active) ? (
                      shouldRenderButtons ? (
                        <CopySVGButton
                          assets={[
                            {
                              slug,
                              collection: iconCollection,
                              media_type: mediaType,
                              is_free: iconCollection === 'basic',
                              format: ['svg'],
                            },
                          ]}
                          is_purchased={is_purchased}
                          isSubscriptionActive={is_subscription_active}
                          onBeforeAction={onBeforeAction}
                        />
                      ) : (
                        <button className="inline-flex items-center text-black hover:underline cursor-pointer">
                          <span>Copy SVG</span>
                        </button>
                      )
                    ) : (
                      <span
                        className="inline-flex items-center text-gray-600 opacity-50 cursor-not-allowed"
                        title="Available after purchase"
                      >
                        Copy SVG
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Similar Icons */}
      {similarIconsData && similarIconsData.items && similarIconsData.items.length > 0 && (
        <SimilarIcons
          slug={slug || ''}
          BACKEND_URL={BACKEND_URL || ''}
          collections={similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d']}
          initialData={similarIconsData || { items: [] }}
        />
      )}
    </>
  );
};

export default IconDetail;